import { supabase } from "@/lib/supabase-client";
import { buildExpenseItemMetaData } from "@/lib/domain/lineItems";

export const billingItemService = {
  async createBillingItems(
    items: {
      client_id: string;
      time_card_id: string;
      amount?: number;
      description?: string;
      rate?: string;
      quantity?: number;
      unit?: string;
      worker?: { id?: string; first_name?: string; last_name?: string };
      shift_id?: string;
      project_id?: string;
      position_name?: string;
      start_time?: string | null;
      end_time?: string | null;
    }[],
  ): Promise<any[]> {
    // Prepare billing items with links in meta_data (IDs only - names are enriched on fetch)
    const billingItemsData = items.map((item) => ({
      client_id: item.client_id,
      meta_data: {
        amount: item.amount,
        description: item.description,
        rate: item.rate, // Formatted rate string: "$35.50/hr" or "$600/day"
        quantity: item.quantity,
        unit: item.unit,
        worker: item.worker, // { id, first_name, last_name }
        shift_id: item.shift_id,
        project_id: item.project_id,
        position_name: item.position_name,
        start_time: item.start_time || null,
        end_time: item.end_time || null,
        links: {
          time_cards: [item.time_card_id],
        },
      },
    }));

    // Insert billing_items
    const { data, error } = await supabase.from("billing_items").insert(billingItemsData).select();

    if (error) {
      throw new Error(`Failed to create billing items: ${error.message}`);
    }

    return data;
  },

  async createBillingItemsFromExpenses(
    items: {
      client_id: string;
      expense_id: string;
      amount?: number;
      description?: string;
      shift_id?: string;
      project_id?: string;
      user_id?: string;
      quantity?: number;
      unit?: string;
    }[],
  ): Promise<any[]> {
    // Fetch user details for worker objects
    const userIds = [...new Set(items.map((item) => item.user_id).filter(Boolean))] as string[];
    let userMap: Record<string, { id: string; first_name: string; last_name: string }> = {};
    if (userIds.length > 0) {
      const { data: users } = await supabase.from("users").select("id, first_name, last_name").in("id", userIds);
      if (users) {
        userMap = Object.fromEntries(
          users.map((u) => [u.id, { id: u.id, first_name: u.first_name, last_name: u.last_name }]),
        );
      }
    }

    // Prepare billing items with links in meta_data
    const billingItemsData = items.map((item) => ({
      client_id: item.client_id,
      meta_data: buildExpenseItemMetaData(item, userMap),
    }));

    // Insert billing_items
    const { data, error } = await supabase.from("billing_items").insert(billingItemsData).select();

    if (error) {
      throw new Error(`Failed to create billing items from expenses: ${error.message}`);
    }

    return data;
  },

  async getBillingItems(includeArchived = false) {
    const { data, error } = await supabase.rpc("get_billing_items", { p_include_archived: includeArchived });
    if (error) throw new Error(`Failed to fetch billing items: ${error.message}`);
    return (data as any[]) || [];
  },

  async getBillingItemsByIds(ids: string[]) {
    if (ids.length === 0) return [];

    const { data, error } = await supabase
      .from("billing_items")
      .select(
        `
        id,
        client_id,
        client: clients(id,name),
        meta_data,
        created_at,
        updated_at
        `,
      )
      .in("id", ids);

    if (error) {
      throw new Error(`Failed to fetch billing items: ${error.message}`);
    }

    return data || [];
  },

  async getBillingItemById(id: string) {
    const { data, error } = await supabase.from("billing_items").select("*").eq("id", id).single();

    if (error) {
      throw new Error(`Failed to fetch billing item: ${error.message}`);
    }

    return data;
  },

  async deleteBillingItemsByIds(ids: string[]): Promise<void> {
    const { error } = await supabase.from("billing_items").delete().in("id", ids);

    if (error) {
      throw new Error(`Failed to delete billing items: ${error.message}`);
    }
  },

  async saveRowPatches(patches: { id: string; [key: string]: any }[]): Promise<void> {
    // Check if any patches contain nested objects (like meta_data) that need merging
    const patchesWithNestedObjects = patches.filter((patch) =>
      Object.values(patch).some(
        (v) => typeof v === "object" && v !== null && !Array.isArray(v) && !(v instanceof Date),
      ),
    );

    // If we have nested objects, we need to fetch current values and merge
    if (patchesWithNestedObjects.length > 0) {
      const idsToFetch = patchesWithNestedObjects.map((p) => p.id);
      const { data: currentRows, error: fetchError } = await supabase
        .from("billing_items")
        .select("id, meta_data")
        .in("id", idsToFetch);

      if (fetchError) {
        throw new Error(`Failed to fetch current values: ${fetchError.message}`);
      }

      const currentDataMap = new Map(currentRows?.map((row) => [row.id, row]) || []);

      // Deep merge nested objects with current values
      for (const patch of patchesWithNestedObjects) {
        const currentRow = currentDataMap.get(patch.id);
        if (currentRow && patch.meta_data) {
          const currentMeta = currentRow.meta_data || {};
          const patchMeta = patch.meta_data;
          const merged = { ...currentMeta, ...patchMeta };

          // Deep merge any sub-objects (e.g. links) so existing keys are preserved
          for (const key of Object.keys(patchMeta)) {
            const patchVal = patchMeta[key];
            const currentVal = currentMeta[key];
            if (
              patchVal &&
              currentVal &&
              typeof patchVal === "object" &&
              !Array.isArray(patchVal) &&
              typeof currentVal === "object" &&
              !Array.isArray(currentVal)
            ) {
              merged[key] = { ...currentVal, ...patchVal };
            }
          }

          patch.meta_data = merged;
        }
      }
    }

    const promises = patches.map(({ id, ...fields }) => supabase.from("billing_items").update(fields).eq("id", id));

    const results = await Promise.all(promises);
    const errors = results.filter((result) => result.error);

    if (errors.length > 0) {
      console.error(
        "Billing item update errors:",
        errors.map((e) => e.error),
      );
      const firstError = errors[0]?.error;
      throw new Error(`Failed to update ${errors.length} billing item(s): ${firstError?.message || "Unknown error"}`);
    }
  },

  async duplicateBillingItem(id: string): Promise<any> {
    // Get the original billing item
    const { data: original, error: fetchError } = await supabase
      .from("billing_items")
      .select("*")
      .eq("id", id)
      .single();

    if (fetchError || !original) {
      throw new Error(`Failed to fetch billing item: ${fetchError?.message || "Not found"}`);
    }

    // Create a duplicate with the same data (excluding id, created_at, updated_at)
    const duplicate = {
      client_id: original.client_id,
      meta_data: original.meta_data,
    };

    const { data, error } = await supabase.from("billing_items").insert([duplicate]).select().single();

    if (error) {
      throw new Error(`Failed to duplicate billing item: ${error.message}`);
    }

    return data;
  },
};
